import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
} from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import TruncatedText from "Components/Common/TruncatedText";
import { normalizeDOB, StudyICFFields } from "helpers/constant";
import { loadCodelists, PrimaryLanguage_OPTIONS, getLabelFromValue } from "api/modules/common.option";
import { showToastMessage } from "common/service/CommonService";
import addVolunteerStyle from "../../../modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import { queryClient } from "api/queryClient";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  InsertMutation?: any;
  onSaveManual?: (record: ICFRecord) => void;
  volunteerid: number;
  studyid: number;
  initialData?: ICFRecord | null;
}

interface FormValues {
  icfversion: string;
  language: string | null;
  signeddate: string | Date;
}

export interface ICFRecord {
  id?: number;
  version: string;
  language: string | null;
  file?: File;
  filename?: string;
  size?: string;
  signeddate: string;
}

const StudyICFAttachmentModal: React.FC<Props> = ({
  isOpen,
  toggle,
  InsertMutation,
  onSaveManual,
  volunteerid,
  studyid,
  initialData,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [icfRecord, setIcfRecord] = useState<ICFRecord | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      icfversion: "",
      language: null,
      signeddate: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (initialData) {
        setIcfRecord(initialData);
        // Find ID if language is stored as label or ID
        const languageId = PrimaryLanguage_OPTIONS.find(
          (opt) => opt.label === initialData.language || String(opt.value) === String(initialData.language)
        )?.value || initialData.language;

        reset({
          icfversion: initialData.version,
          language: languageId ? String(languageId) : null,
          signeddate: initialData.signeddate ? new Date(initialData.signeddate) : "",
        });
      } else {
        setIcfRecord(null);
        reset({
          icfversion: "",
          language: null,
          signeddate: "",
        });
      }
    }
  }, [isOpen, initialData, reset]);

  /* ================= FILE UPLOAD ================= */

  const handleAddAttachment = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // File type validation (PDF only)
    const isPDF = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPDF) {
      showToastMessage("error", "Invalid file format. Please upload a PDF file.");
      e.target.value = ""; // Reset file input
      return;
    }

    // File size validation (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      showToastMessage("error", "File size exceeds 10MB limit. Please upload a smaller file.");
      e.target.value = ""; // Reset file input
      return;
    }

    const values = getValues();
    
    const newRecord: ICFRecord = {
      id: icfRecord?.id || Date.now(),
      version: values.icfversion,
      language: values.language,
      signeddate: normalizeDOB(values.signeddate),
      file,
      filename: file.name,
      size: `${(file.size / 1024).toFixed(2)} KB`,
    };

    setIcfRecord(newRecord);
  };

  /* ================= PREVIEW ================= */

  const handlePreview = (file: File) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  /* ================= DELETE ================= */

  const removeICFRecord = () => {
    setIcfRecord(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* ================= LOAD CODELIST ================= */

  useEffect(() => {
    const load = async () => {
      const result = await loadCodelists(["PrimaryLanguage_OPTIONS"]);

      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
    };

    load();
  }, []);

  /* ================= SUBMIT ================= */

  const onSubmit = (data: FormValues) => {
    // if (!icfRecord) {
    //   showToastMessage("error", "Please upload ICF file");
    //   return;
    // }

    const updatedRecord: ICFRecord = {
      ...(icfRecord || {}),
      version: data.icfversion,
      language: data.language,
      signeddate: normalizeDOB(data.signeddate),
    } as ICFRecord;

    if (onSaveManual) {
      onSaveManual(updatedRecord);
      toggle();
      return;
    }

    if (!InsertMutation) {
      showToastMessage("error", "InsertMutation is not defined");
      return;
    }

    const formData = new FormData();
    formData.append("volunteerid", String(volunteerid));
    formData.append("studyid", String(studyid));
    formData.append("icfversion", data.icfversion);
    formData.append("language", String(data.language || ""));
    formData.append("signeddate", normalizeDOB(data.signeddate));
    
    if (icfRecord?.file) {
      formData.append("files", icfRecord.file);
    }

    InsertMutation.mutate(formData, {
      onSuccess: () => {
        showToastMessage("success", "ICF Uploaded Successfully");
        setIcfRecord(null);
        queryClient.refetchQueries({ queryKey: ["volunteer-study-icf-selectAll"] });
        toggle();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        scrollable
        size="lg"
        backdrop="static"
        className="common_modal"
      >
        <ModalHeader toggle={toggle} className="p-3">
          Study ICF Attachment
        </ModalHeader>

        <ModalBody>
          {/* FORM */}

          <Row>
            <Col lg="12">
              <CommonForm
                fields={StudyICFFields}
                control={control}
                errors={errors}
                columns={2}
                setValue={setValue}
              />
            </Col>
          </Row>

          {/* FILE UPLOAD */}

          <div className="mt-3">
            <h6 className="mb-2">ICF Attachment</h6>

            <div className="icf-upload-box" onClick={handleAddAttachment}>
              <i className="ri-upload-cloud-2-line fs-3 text-primary"></i>
              <p className="mb-0 mt-2">Click or Drag file to upload ICF</p>
              <small className="text-muted">PDF only</small>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>

          {/* ICF LIST */}

          <Row className="mt-3">
            {!icfRecord || !icfRecord.filename ? (
              <div className="text-muted small mt-2">
                No attachment uploaded.
              </div>
            ) : (
              <Col
                className={`p-0 ${addVolunteerStyle.attachmentCard_wrapper}`}
              >
                <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                  <div className={addVolunteerStyle.fileInfo}>
                    <div className={addVolunteerStyle.fileIcon}>
                      <i className="ri-file-pdf-line text-danger" />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={addVolunteerStyle.fileName}>
                        <TruncatedText text={icfRecord.filename} />
                      </div>

                      <div className={addVolunteerStyle.fileMeta}>
                        {icfRecord.size}
                      </div>

                      <div className="small text-muted">
                        Version: {icfRecord.version} | Language:{" "}
                        {isNaN(Number(icfRecord.language))
                          ? (icfRecord.language || "English")
                          : getLabelFromValue(PrimaryLanguage_OPTIONS, icfRecord.language, "English")}
                      </div>

                      <div className="small text-muted">
                        Signed Date:{" "}
                        {new Date(icfRecord.signeddate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className={addVolunteerStyle.fileActions}>
                    <i
                      className="ri-eye-line"
                      title="Preview"
                      onClick={() => icfRecord.file && handlePreview(icfRecord.file)}
                    />

                    <i
                      className="ri-delete-bin-line text-danger"
                      title="Delete"
                      onClick={removeICFRecord}
                    />
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </ModalBody>

        <ModalFooter className="bg-white border-top">
          <Button outline onClick={toggle}>
            Cancel
          </Button>

          <Button color="primary" onClick={handleSubmit(onSubmit)} outline>
            Save ICF
          </Button>
        </ModalFooter>
      </Modal>

      <FilePreviewModal
        file={previewFile}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
};

export default StudyICFAttachmentModal;
